/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { AdBreakInfo } from './AdBreakInfo';

export const TIME_VALUE_UNKNOWN = -1.0;
export const VALUE_UNKNOWN = -99999; // minimum integer value set when null.

export type AdType = 'preroll' | 'midroll' | 'postroll';

export enum AdContentTimePosition {
  /**
   * Indicates that the video ad plays before the content starts.
   */
  PREROLL = 'preroll',
  /**
   * Indicates that the video ad plays in the middle of content.
   */
  MIDROLL = 'midroll',
  /**
   *  Indicates that the video ad plays after the content has been played.
   */
  POSTROLL = 'postroll',

  /**
   *  Indicates unknown time position.
   */
  UNKNOWN = 'unknown',
}

export type AdState =
  | 'idle'
  | 'loading'
  | 'loaded'
  | 'started'
  | 'playing'
  | 'paused'
  | 'buffering'
  | 'completed'
  | 'failed'
  | 'skipped'
  | 'resumed';

/**
 * Represents the Video Ad Serving Template -VAST- properties
 * to be shared with analytic providers.
 */
export interface VASTProperties {
  /**
   * The source ad server of the advert.
   */
  adSystem?: string;
  /**
   * The common name of the advert.
   */
  adTitle?: string;
  /**
   * The identifier used to compare impression-level data .
   * across systems
   */
  adServingId?: string;
  /**
   * The identifier of the creative.
   */
  creativeId?: string;
  /**
   * The category of the advert content.
   */
  category?: string;
  /**
   * The longer description of the advert.
   */
  description?: string;
  /**
   * The advertiser name.
   */
  advertiser?: string;
  /**
   * The advertiser identifier.
   */
  advertiserId?: string;
  /**
   * The pricing element of the advert.
   */
  pricing?: string;
  /**
   * The URI to any resource file having to do with
   * collecting survey data.
   */
  survey?: string;
  /**
   * The number of seconds in which the ad is valid for
   * execution.
   */
  expires?: string;
  /**
   * The bitrate for the selected media file.
   */
  bitrate?: number;
  /**
   * The List of AdChoicesIcon.
   */
  industryIcons?: IndustryIcon[];

  /**
   * The custom VAST extensions properties.
   */
  extensions?: Extensions;

  /**
   * Visit advertiser page endpoint.
   */
  videoClickThroughURL?: string;

  /**
   * Visit advertiser page click tracking.
   */
  videoClickTrackingURLs?: string[];

  /**
   * The API framework of the selected media file (e.g., VPAID, OMID).
   */
  apiFramework?: string;

  /**
   * The ids of the wrapper ads that led to this advert, outermost first.
   */
  wrapperAdIds?: string[];

  /**
   * The ad systems of the wrapper ads that led to this advert, outermost first.
   */
  wrapperAdSystems?: string[];

  /**
   * The creative ids of the wrapper ads that led to this advert, outermost first.
   */
  wrapperCreativeIds?: string[];
}

/**
 * Describes how an advert was requested and delivered, for analytic providers.
 */
export interface AdDeliveryProperties {
  /**
   * Whether the advert is stitched into the stream or inserted by the client.
   */
  technology?: 'client-side' | 'server-side';
  /**
   * The component that stitched or inserted the advert (e.g., Google IMA SDK, Yospace).
   */
  stitcher?: string;
  /**
   * The name of the ad manager library.
   */
  managerName?: string;
  /**
   * The version of the ad manager library.
   */
  managerVersion?: string;
  /**
   * The URL the advert was requested with.
   */
  adTagUrl?: string;
}

export interface Extensions {
  clickFallbackImages?: IconClickFallbackImage[];
}

/**
 * Represents the ad break information.
 */
export interface AdBreak {
  /**
   * The ad position of the ad break.
   */
  position: AdContentTimePosition;
  /**
   * Start time of the ad break.
   */
  startTime: number;
  /**
   * Duration of the ad break.
   */
  duration: number;
  /**
   * Number of ads with the ad break.
   */
  totalAds: number;
  /**
   * Whether the ad break is active or not.
   */
  active: boolean;
  /**
   * A flag indicating whether the ad is skippable or not.
   */
  isSkippable?: boolean;
}

/**
 * Represents the ad information.
 */
export interface Ad {
  /**
   * The identifier of the ad.
   */
  adId: string;
  /**
   * The sequence of the ad within the ad break.
   */
  sequence: number;
  /**
   * The start position of the ad.
   */
  start: number;
  /**
   * The duration of the ad.
   */
  duration: number;
  /**
   * The title of the ad.
   */
  title: string;
  /**
   * A flag indicating whether the ad is skippable or not.
   */
  isSkippable: boolean;
  /**
   * A flag indicating whether the ad will play automatically or not.
   */
  isAutoPlay?: boolean;
  /**
   * The skip offset of the ad.
   */
  skipOffset?: number;
  /**
   * The associated ad break of the ad.
   */
  adBreak?: AdBreak;
  /**
   * The Video Ad Serving Template -VAST- properties.
   */
  vastProperties?: VASTProperties;
  /**
   * The original content duration (without ads) in milliseconds.
   */
  contentDurationMs?: number;
  /**
   * The players current time with respect to origin content.
   */
  contentPlayheadPositionMs?: number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface AdRequest {}

/** A static companion creative supplied with an advert. */
export interface CompanionAd {
  id?: string;
  /** Creative width in pixels. */
  width: number;
  /** Creative height in pixels. */
  height: number;
  /** URL of the companion image. */
  staticResource: string;
  clickThroughUrl?: string;
  /** URLs the companion renderer requests when the creative is displayed. */
  impressionTrackingUrls?: string[];
  /** URLs the companion renderer requests when the creative is clicked. */
  clickTrackingUrls?: string[];
}

/**
 * Represents the ad information.
 */
export interface AdInfo {
  /**
   * The identifier of the ad.
   */
  adID: string;
  /**
   * The sequence of the ad within the ad break.
   */
  sequence: number;
  /**
   * The start position of the ad.
   */
  adStartTimeOffsetMs?: number;
  /**
   * The duration of the ad.
   */
  durationMs?: number;
  /**
   * The remaining time of the ad.
   */
  remainingTimeMs?: number;
  /**
   * A flag indicating whether the ad is skippable or not.
   */
  isSkippable: boolean;
  /**
   * The skip offset of the ad.
   */
  skipOffsetMs?: number;
  /**
   * A flag indicating whether the ad is filler or not.
   */
  isFiller?: boolean;
  /**
   * media url of the current playing Ad.
   */
  mediaUrl?: string;
  /**
   * The associated ad break of the ad.
   */
  adBreakInfo?: AdBreakInfo;
  /**
   * The Video Ad Serving Template -VAST- properties.
   */
  vastProperties?: VASTProperties;
  /**
   * The raw trafficking (ad) parameters of the ad, a comma separated list of
   * `key=value` segments used to drive companion creatives.
   */
  traffickingParameters?: string;
  /** Static companions available for app or SDK presentation. */
  companionAds?: CompanionAd[];
  /**
   * How the advert was requested and delivered.
   */
  delivery?: AdDeliveryProperties;
  /**
   * Unique identifier (UUID) of this ad, supplied by the ad player. Analytics providers use it to
   * correlate the ad's events.
   */
  adSessionId?: string;
}

/**
 * Represents an industry icon object.
 * Encapsulates the data from the <Icon> element of a VAST document.
 * It is a graphic that overlays a small portion of the advert's video and is sometimes clickable.
 */
export interface IndustryIcon {
  /**
   * The program represented in the icon (e.g. "AdChoices").
   */
  program?: string;
  /**
   * The pixel width of the icon asset.
   */
  width?: string;
  /**
   * The pixel height of the icon asset.
   */
  height?: string;
  /**
   * The x-coordinate of the top, left corner of the icon
   * asset relative to the ad display area. Values of "left" or "right" also
   * accepted and indicate the leftmost or rightmost available position for
   * the icon asset.
   */
  xPosition?: string;
  /**
   * The y-coordinate of the top left corner of the icon asset
   * relative to the ad display area; values of"top" or "bottom" also accepted and
   * indicate the topmost or bottommost available position for the icon asset.
   */
  yPosition?: string;
  /**
   * The duration of the icon appears, in seconds.
   */
  duration?: number;
  /**
   * Time when in the video the icon appears, in seconds.
   */
  offset?: number;
  /**
   * Identifies the API needed to execute the icon resource
   * file if applicable.
   */
  apiFramework?: string;
  /**
   * The alternative text for the image. In an html5 image tag
   * this should be the text for the alt attribute. This should enable screen
   * readers to properly read back a description of the icon for visually
   * impaired users.
   */
  altText?: string;
  /**
   * Hover text for the image.
   * In an html5 image tag this should be the text for the title attribute.
   */
  hoverText?: string;
  /**
   * The URL of the image file for the icon (PNG is preferred).
   */
  imageURL?: string;
  /**
   * he URL of the action that occurs when a user
   * clicks the AdChoices icon. In most cases,the user sees the "Ad Settings" page.
   * that the media player opens when the icon is clicked.
   */
  clickThroughURL?: string;
  /**
   * The list of IconClickFallbackImage instance.
   */
  fallbackImages?: IconClickFallbackImage[];
}

/**
 * Represents an icon click fallback image object.
 * Encapsulates the data from the <IconClickFallbackImage> element of a VAST document.
 * It is a graphic that is used when a target platform does not support HTML rendering: the information is provided as an image instead.
 */
export interface IconClickFallbackImage {
  /**
   * The pixel width of the image asset.
   */
  width?: string;
  /**
   * The pixel height of the image asset.
   */
  height?: string;
  /**
   * The alternative text for the fallback image.
   */
  altText?: string;
  /**
   * The URL of the image file for the fallback image
   * (PNG is preferred).
   */
  imageURL?: string;

  /**
   * The program represented in the icon (e.g. "AdChoices").
   */

  program?: string;
}

export enum IndustryIconProperty {
  PROGRAM = 'program',
  WIDTH = 'width',
  HEIGHT = 'height',
  XPOSITION = 'xPosition',
  YPOSITION = 'yPosition',
  DURATION = 'duration',
  OFFSET = 'offset',
  APIFRAMEWORK = 'apiFramework',
  ALTTEXT = 'altText',
  HOVERTEXT = 'hoverText',
}

export enum IconClickFallbackImageProperty {
  PROGRAM = 'program',
  WIDTH = 'width',
  HEIGHT = 'height',
  ALTTEXT = 'AltText',
  TYPE = 'type',
  ICONCLICKFALLBACKIMAGES = 'IconClickFallbackImages',
  STATICRESOURCE = 'StaticResource',
}
